import { defineStore } from 'pinia';
import { fetchMyBranding } from '@/api/branding';
import { applyBrandingCssVars, clearBrandingCssVars } from '@/composables/useApplyBrandingCssVars';
import platformLogoAsset from '@/assets/images/logo-mini.png';

/** Logo empacotada pelo Vite (funciona em dev e build). */
export const PLATFORM_LOGO_SRC = platformLogoAsset;
const FALLBACK_NAME = 'Descomplique sua correção';

export const useBrandingStore = defineStore('branding', {
    state: () => ({
        loaded: false,
        loading: false,
        error: null,
        professorId: null,
        displayName: FALLBACK_NAME,
        logoUrl: null,
        authCoverImageUrl: null,
        primaryColor: '#4361ee',
        accentColor: '#0d6efd',
        platformName: FALLBACK_NAME,
        mode: 'co_brand',
        isCustomized: false,
        context: null,
        platformLogoUrl: null,
        portalSlug: null,
        portalEnabled: false,
    }),

    getters: {
        effectiveLogo(state) {
            return state.logoUrl || PLATFORM_LOGO_SRC;
        },
        effectiveDisplayName(state) {
            return state.displayName || FALLBACK_NAME;
        },
        /** Estilo inline para .l-image / .l-image-register quando há capa customizada. */
        authCoverStyle(state) {
            if (!state.authCoverImageUrl) return null;
            const safe = String(state.authCoverImageUrl).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
            return {
                backgroundImage: `url("${safe}")`,
                backgroundPosition: 'center center',
                backgroundSize: 'cover',
            };
        },
        /** Logo da marca do professor se customizada; senão logo da plataforma. */
        chromeLogo(state) {
            if (state.logoUrl) return state.logoUrl;
            return PLATFORM_LOGO_SRC;
        },
        chromeTitle(state) {
            if (state.isCustomized && state.displayName) return state.displayName;
            return FALLBACK_NAME;
        },
    },

    actions: {
        /**
         * Busca branding autenticado (`/me/branding`) e aplica CSS vars.
         * @param {{ turmaId?: string|null, professorId?: string|null, signal?: AbortSignal, force?: boolean }} [options]
         */
        async load(options = {}) {
            const force = Boolean(options.force);
            // Sem force: reutiliza request em voo. Com force: espera o atual e refetch.
            if (this.loading && this._loadPromise && !force) {
                return this._loadPromise;
            }
            if (this.loading && this._loadPromise && force) {
                try {
                    await this._loadPromise;
                } catch {
                    /* ignore */
                }
            }

            this.loading = true;
            this.error = null;
            this._loadPromise = (async () => {
                try {
                    const data = await fetchMyBranding(options);
                    this.applyPayload(data);
                    this.loaded = true;
                } catch (e) {
                    this.error = e;
                    if (force || !this.isCustomized) {
                        clearBrandingCssVars();
                    }
                } finally {
                    this.loading = false;
                    this._loadPromise = null;
                }
            })();

            return this._loadPromise;
        },
        /**
         * Aplica payload já obtido (ex.: link público de turma).
         * @param {Record<string, unknown>|null|undefined} data
         */
        applyPayload(data) {
            if (!data || typeof data !== 'object') {
                this.resetToPlatform();
                return;
            }
            this.professorId = data.professor_id != null ? String(data.professor_id) : null;
            this.displayName = typeof data.display_name === 'string' && data.display_name
                ? data.display_name
                : FALLBACK_NAME;
            this.logoUrl = typeof data.logo_url === 'string' && data.logo_url ? data.logo_url : null;
            this.authCoverImageUrl =
                typeof data.auth_cover_image_url === 'string' && data.auth_cover_image_url
                    ? data.auth_cover_image_url
                    : null;
            this.primaryColor = typeof data.primary_color === 'string' ? data.primary_color : '#4361ee';
            this.accentColor = typeof data.accent_color === 'string' ? data.accent_color : '#0d6efd';
            this.platformName =
                typeof data.platform_name === 'string' && data.platform_name.trim()
                    ? data.platform_name.trim()
                    : FALLBACK_NAME;
            this.mode = data.mode === 'white_label' ? 'white_label' : 'co_brand';
            this.isCustomized = Boolean(data.is_customized);
            this.context = typeof data.context === 'string' ? data.context : null;
            this.platformLogoUrl =
                typeof data.platform_logo_url === 'string' ? data.platform_logo_url : null;
            this.portalSlug =
                typeof data.portal_slug === 'string' && data.portal_slug.trim()
                    ? data.portal_slug.trim()
                    : null;
            this.portalEnabled = Boolean(data.portal_enabled);
            this.loaded = true;
            applyBrandingCssVars({
                primary_color: this.primaryColor,
                accent_color: this.accentColor,
                mode: this.mode,
                is_customized: this.isCustomized,
            });
        },

        resetToPlatform() {
            this.professorId = null;
            this.displayName = FALLBACK_NAME;
            this.logoUrl = null;
            this.authCoverImageUrl = null;
            this.primaryColor = '#4361ee';
            this.accentColor = '#0d6efd';
            this.platformName = FALLBACK_NAME;
            this.mode = 'co_brand';
            this.isCustomized = false;
            this.context = 'platform';
            this.platformLogoUrl = null;
            this.portalSlug = null;
            this.portalEnabled = false;
            this.loaded = true;
            clearBrandingCssVars();
        },

        /** Garante CSS vars da conta após login (mesmo se portal/auth já marcou loaded). */
        async ensureAccountBranding() {
            return this.load({ force: true });
        },
    },
});
